import {
  FeatureFlag,
  UserLevelResult,
  WebDashboardTrialHistoryEntry,
  WebDashboardTrialStateType,
  WebDashboardTrialUserRecord,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { AutoComplete, DatePicker, message, Modal, Select, Table, Tag } from "antd";
import moment from "moment";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { DEPRECATED_Button, Form, FormItem, Input, TextArea } from "../../components";
import { COLORS } from "../../constants";
import { useApiContext } from "../../utils";

import { getSelectedUsersMaxEndDt, isExpirationValid } from "./webDashboardTrials.utils";

const Layout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const FilterBarLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.black45};
  white-space: nowrap;
`;

const LeftPanel = styled.aside`
  width: 280px;
  min-width: 280px;
  padding: 24px 16px;
  border-right: 1px solid #f0f0f0;
  overflow-y: auto;
  height: 100%;
`;

const MainPanel = styled.main`
  flex: 1;
  padding: 24px;
  overflow: auto;
  min-width: 0;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.black45};
  margin: 0 0 12px 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 20px 0;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 20px 0;
`;

// In the user tab, admins may only put users into a manual countdown (with an
// extended expiry). Admins behind the trial-admin-overrides gate may additionally
// expire users manually.
const USER_SETTABLE_STATES = [WebDashboardTrialStateType.TrialManualCountdown];
const OVERRIDE_SETTABLE_STATES = [
  WebDashboardTrialStateType.TrialManualCountdown,
  WebDashboardTrialStateType.TrialManualExpired,
];

const STATE_COLOR: Record<string, string> = {
  [WebDashboardTrialStateType.TrialUnlimited]: "green",
  [WebDashboardTrialStateType.TrialCountdown]: "blue",
  [WebDashboardTrialStateType.TrialManualCountdown]: "blue",
  [WebDashboardTrialStateType.TrialManualExpired]: "red",
  [WebDashboardTrialStateType.TrialExpired]: "red",
  [WebDashboardTrialStateType.Paid]: "purple",
  [WebDashboardTrialStateType.Cancelled]: "default",
};

const PAGE_SIZE = 50;

const Dash = () => <span style={{ color: COLORS.black45 }}>—</span>;

const StateTag = ({ state }: { state?: string | null }) =>
  state ? <Tag color={STATE_COLOR[state] ?? "default"}>{state}</Tag> : <Dash />;

const HISTORY_COLUMNS = [
  {
    title: "Date",
    dataIndex: "created_dt",
    key: "created_dt",
    width: 200,
    render: (dt: string) => (
      <span style={{ whiteSpace: "nowrap" }}>{new Date(dt).toLocaleString()}</span>
    ),
  },
  {
    title: "Transition",
    key: "transition",
    render: (_: unknown, h: WebDashboardTrialHistoryEntry) => (
      <span>
        {h.old_trial_state ? (
          <>
            <StateTag state={h.old_trial_state} />→{" "}
          </>
        ) : null}
        <StateTag state={h.new_trial_state} />
      </span>
    ),
  },
  {
    title: "By",
    dataIndex: "modified_by_email",
    key: "modified_by_email",
    width: 180,
    render: (email: string | undefined) => email ?? <Dash />,
  },
  {
    title: "Note",
    dataIndex: "transition_note",
    key: "transition_note",
    render: (note: string | undefined) => note ?? <Dash />,
  },
];

// "View History" modal used by the User tab.
const useTrialHistory = () => {
  const { apiClient } = useApiContext();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<React.ReactNode>("Trial History");
  const [history, setHistory] = useState<WebDashboardTrialHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const openHistory = useCallback(
    async (productTrialId: string, titleNode: React.ReactNode) => {
      setTitle(titleNode);
      setHistory([]);
      setOpen(true);
      setLoading(true);
      try {
        const res = await apiClient.getWebDashboardTrialHistory({
          product_trial_id: productTrialId,
        });
        setHistory(res.data.history);
      } catch {
        message.error("Failed to load trial history");
      } finally {
        setLoading(false);
      }
    },
    [apiClient]
  );

  const historyModal = (
    <Modal
      title={title}
      open={open}
      onCancel={() => {
        setOpen(false);
        setHistory([]);
      }}
      footer={null}
      width={920}
    >
      <Table
        columns={HISTORY_COLUMNS}
        dataSource={history}
        rowKey="product_trial_history_id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: "No history found" }}
      />
    </Modal>
  );

  return { openHistory, historyModal };
};

const UserTrialsTab: React.FC = () => {
  const { apiClient, coeffAdminApiClient } = useApiContext();
  const { openHistory, historyModal } = useTrialHistory();
  const { value: adminOverridesEnabled } = useFeatureGate(
    FeatureFlag.EnableWebDashboardTrialAdminOverrides
  );

  const [domainInput, setDomainInput] = useState("");
  const [domainOptions, setDomainOptions] = useState<{ value: string }[]>([]);
  const [emailsInput, setEmailsInput] = useState("");
  // Describes the last successful load (for the header + refresh after a bulk update). `label` is
  // for display; `query` is replayed verbatim to refetch.
  const [loadedLabel, setLoadedLabel] = useState("");
  const [lastQuery, setLastQuery] = useState<{ domain?: string; emails?: string } | null>(null);
  const [users, setUsers] = useState<WebDashboardTrialUserRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE });

  const [newTrialState, setNewTrialState] = useState<WebDashboardTrialStateType | undefined>(
    WebDashboardTrialStateType.TrialManualCountdown
  );
  const [note, setNote] = useState("");
  const [expirationDate, setExpirationDate] = useState<moment.Moment | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const settableStates = adminOverridesEnabled ? OVERRIDE_SETTABLE_STATES : USER_SETTABLE_STATES;
  // Manual-expired needs no end date; only manual countdown takes an expiry.
  const needsExpiration = newTrialState === WebDashboardTrialStateType.TrialManualCountdown;

  // Latest current expiry among the selected users — the new expiry is required and must extend
  // past it, so a bulk update can never shorten an active trial (own or domain-inherited).
  // Admins behind the overrides gate may shorten a trial to any future date.
  const currentMaxEndDt = getSelectedUsersMaxEndDt(users, selectedEmails);
  const expirationValid = isExpirationValid(expirationDate, currentMaxEndDt, adminOverridesEnabled);

  const searchDomains = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (trimmed.length < 2) {
        setDomainOptions([]);
        return;
      }
      try {
        const res = await coeffAdminApiClient.getDomains({ search_by: trimmed, page_size: 25 });
        setDomainOptions(res.data.domains.map(d => ({ value: d.domain })));
      } catch {
        setDomainOptions([]);
      }
    },
    [coeffAdminApiClient]
  );

  const loadUsers = useCallback(
    async (query: { domain?: string; emails?: string }, label: string) => {
      setLoadingUsers(true);
      try {
        const res = await apiClient.getWebDashboardTrialUsers(query);
        setUsers(res.data.users);
        setLoadedLabel(label);
        setLastQuery(query);
        setSelectedEmails([]);
        setPagination(p => ({ ...p, current: 1 }));
      } catch (err) {
        const serverMsg = (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
        message.error(serverMsg ?? "Failed to load users");
      } finally {
        setLoadingUsers(false);
      }
    },
    [apiClient]
  );

  const fetchUsers = useCallback(
    (domain: string) => {
      const trimmed = domain.trim().toLowerCase();
      if (!trimmed) {
        return;
      }
      loadUsers({ domain: trimmed }, trimmed);
    },
    [loadUsers]
  );

  // Emails are entered comma-separated; split, trim, and drop blanks before sending.
  const fetchUsersByEmails = useCallback(() => {
    const emails = emailsInput
      .split(",")
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    if (emails.length === 0) {
      return;
    }
    loadUsers({ emails: emails.join(",") }, `${emails.length} email(s)`);
  }, [loadUsers, emailsInput]);

  const canSubmit =
    selectedEmails.length > 0 &&
    newTrialState !== undefined &&
    note.trim() !== "" &&
    (!needsExpiration || expirationValid);

  const handleMoveTo = async () => {
    if (!canSubmit || newTrialState === undefined) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.retoolWebDashboardTrialBulkUpdate({
        data: {
          user_emails: selectedEmails,
          new_trial_state: newTrialState,
          note: note.trim(),
          ...(newTrialState === WebDashboardTrialStateType.TrialManualCountdown && expirationDate
            ? { trial_state_end_dt: expirationDate.toISOString() }
            : {}),
        },
      });
      const results: UserLevelResult[] = res.data.result_by_user;
      const failures = results.filter(r => !r.is_success);
      if (failures.length === 0) {
        message.success(`Updated ${results.length} user(s)`);
      } else {
        message.warning(
          `${results.length - failures.length} succeeded, ${failures.length} failed: ` +
            failures.map(f => `${f.email} (${f.message ?? "error"})`).join("; ")
        );
      }
      setNote("");
      setExpirationDate(null);
      if (lastQuery) {
        await loadUsers(lastQuery, loadedLabel);
      }
    } catch {
      message.error("Failed to update trial state");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = emailFilter.trim()
    ? users.filter(u => u.email.toLowerCase().includes(emailFilter.trim().toLowerCase()))
    : users;

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a: WebDashboardTrialUserRecord, b: WebDashboardTrialUserRecord) =>
        a.email.localeCompare(b.email),
    },
    {
      title: "Trial State",
      dataIndex: "trial_state",
      key: "trial_state",
      width: 260,
      sorter: (a: WebDashboardTrialUserRecord, b: WebDashboardTrialUserRecord) =>
        (a.trial_state ?? "").localeCompare(b.trial_state ?? ""),
      render: (state: string | undefined, record: WebDashboardTrialUserRecord) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <StateTag state={state} />
          {record.is_domain_default && (
            <span style={{ fontSize: 12, fontStyle: "italic", color: COLORS.black45 }}>
              from domain trial
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Entered At",
      dataIndex: "trial_state_entered_dt",
      key: "trial_state_entered_dt",
      width: 180,
      render: (dt: string | undefined) => (dt ? new Date(dt).toLocaleString() : <Dash />),
    },
    {
      title: "End At",
      dataIndex: "trial_state_end_dt",
      key: "trial_state_end_dt",
      width: 180,
      render: (dt: string | undefined) => (dt ? new Date(dt).toLocaleString() : <Dash />),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_: unknown, record: WebDashboardTrialUserRecord) =>
        record.product_trial_id ? (
          <DEPRECATED_Button
            size="small"
            onClick={() =>
              openHistory(
                record.product_trial_id as string,
                <span>
                  Trial History —{" "}
                  <span style={{ fontWeight: 400, fontSize: 13 }}>{record.email}</span>
                </span>
              )
            }
          >
            View History
          </DEPRECATED_Button>
        ) : (
          <Dash />
        ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Layout>
        <LeftPanel>
          <SectionTitle>Filter by domain</SectionTitle>
          <AutoComplete
            style={{ width: "100%" }}
            placeholder="Search domain… e.g. company.com"
            value={domainInput}
            options={domainOptions}
            onSearch={searchDomains}
            onChange={val => setDomainInput(val)}
            onSelect={(val: string) => fetchUsers(val)}
            allowClear
          />
          <DEPRECATED_Button
            style={{ marginTop: 8, width: "100%" }}
            onClick={() => fetchUsers(domainInput)}
            loading={loadingUsers}
          >
            Load Users
          </DEPRECATED_Button>

          <Divider />

          <SectionTitle>Search by emails</SectionTitle>
          <TextArea
            style={{ width: "100%" }}
            placeholder="Comma-separated emails — gmail / personal-email users only"
            value={emailsInput}
            onChange={e => setEmailsInput(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
            allowClear
          />
          <DEPRECATED_Button
            style={{ marginTop: 8, width: "100%" }}
            onClick={fetchUsersByEmails}
            loading={loadingUsers}
          >
            Load Users
          </DEPRECATED_Button>

          <Divider />

          <SectionTitle>Bulk Actions</SectionTitle>
          <div style={{ fontSize: 13, color: COLORS.black45, marginBottom: 12 }}>
            {selectedEmails.length} user(s) selected
          </div>
          <Form layout="vertical">
            <FormItem label="New Trial State" required>
              <Select
                style={{ width: "100%" }}
                placeholder="Select state"
                value={newTrialState}
                onChange={val => setNewTrialState(val)}
              >
                {settableStates.map(s => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>

            {needsExpiration && (
              <FormItem
                label={
                  <span>
                    Expiration Date <span style={{ color: "red" }}>*</span>
                  </span>
                }
                help={
                  adminOverridesEnabled
                    ? "Must be in the future — shortening the current trial is allowed"
                    : currentMaxEndDt
                    ? `Must be after the current expiry (${currentMaxEndDt
                        .toDate()
                        .toLocaleString()})`
                    : undefined
                }
                validateStatus={expirationDate !== null && !expirationValid ? "error" : undefined}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  showTime
                  value={expirationDate}
                  onChange={date => setExpirationDate(date)}
                  placeholder="Trial end date"
                  disabledDate={current =>
                    current !== null &&
                    (adminOverridesEnabled
                      ? current.isBefore(moment(), "day")
                      : currentMaxEndDt !== null && current.isBefore(currentMaxEndDt, "day"))
                  }
                />
              </FormItem>
            )}

            <FormItem
              label={
                <span>
                  Note <span style={{ color: "red" }}>*</span>
                </span>
              }
            >
              <Input.TextArea
                placeholder="Reason for this change"
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
              />
            </FormItem>

            <DEPRECATED_Button
              type="primary"
              disabled={!canSubmit}
              loading={submitting}
              onClick={handleMoveTo}
              style={{ width: "100%" }}
            >
              Move to
            </DEPRECATED_Button>
          </Form>
        </LeftPanel>

        <MainPanel>
          <FilterBar style={{ padding: "0 0 16px 0", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <FilterBarLabel>Filter by email</FilterBarLabel>
              <Input
                style={{ width: 280 }}
                placeholder="Search loaded users by email…"
                value={emailFilter}
                onChange={e => {
                  setEmailFilter(e.target.value);
                  setPagination(p => ({ ...p, current: 1 }));
                }}
                allowClear
              />
            </div>
            {loadedLabel && (
              <span style={{ fontSize: 13, color: COLORS.black45 }}>
                Showing {filteredUsers.length} of {users.length} user(s) for{" "}
                <strong>{loadedLabel}</strong>
              </span>
            )}
          </FilterBar>
          <Table
            columns={columns}
            dataSource={filteredUsers}
            rowKey="email"
            loading={loadingUsers}
            rowSelection={{
              selectedRowKeys: selectedEmails,
              onChange: keys => setSelectedEmails(keys as string[]),
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showTotal: total => `${total} users`,
              showSizeChanger: true,
              onChange: (current, pageSize) => setPagination({ current, pageSize }),
            }}
            scroll={{ x: "auto" }}
            size="small"
            locale={{
              emptyText: loadedLabel ? "No users found" : "Load a domain or emails to see users",
            }}
          />
        </MainPanel>
      </Layout>

      {historyModal}
    </div>
  );
};

export const WebDashboardTrials: React.FC = () => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <div style={{ padding: "24px 24px 0" }}>
      <PageTitle>Web Dashboard Trials</PageTitle>
    </div>
    <div style={{ flex: 1, minHeight: 0 }}>
      <UserTrialsTab />
    </div>
  </div>
);
